import { useEffect, useRef } from "react";

/**
 * Drift — a real-time particle flow field on <canvas>.
 * Particles follow a pseudo-noise vector field and bend around the pointer.
 * Pure client-side, no dependencies. Tuned for the light "paper" theme.
 */
export default function FlowField() {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const pointer = useRef({ x: -9999, y: -9999, active: false });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let width = 0;
    let height = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let particles = [];

    // Palette (paper theme)
    const PAPER = "#FAF9F5";
    const INK = "22, 20, 15"; // rgb of #16140F

    const noise = (x, y, t) =>
      Math.sin(x * 0.0016 + t) * Math.cos(y * 0.0016 - t * 0.8) +
      0.5 * Math.sin((x + y) * 0.0011 + t * 1.3);

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = PAPER;
      ctx.fillRect(0, 0, width, height);
      seed();
    };

    const seed = () => {
      const count = Math.round(Math.min(900, Math.max(280, (width * height) / 1400)));
      particles = new Array(count).fill(0).map(() => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: 0,
        vy: 0,
        life: Math.random() * 200,
      }));
    };

    let t = 0;
    const step = () => {
      t += 0.0015;
      // translucent paper wash → fading trails
      ctx.fillStyle = "rgba(250, 249, 245, 0.055)";
      ctx.fillRect(0, 0, width, height);

      const p = pointer.current;
      for (const pt of particles) {
        const angle = noise(pt.x, pt.y, t) * Math.PI * 2;
        let ax = Math.cos(angle) * 0.18;
        let ay = Math.sin(angle) * 0.18;

        // pointer influence (swirl + push)
        if (p.active) {
          const dx = pt.x - p.x;
          const dy = pt.y - p.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < 28000) {
            const d = Math.sqrt(d2) || 1;
            const force = (1 - d / 168) * 1.6;
            ax += (-dy / d) * force + (dx / d) * force * 0.35;
            ay += (dx / d) * force + (dy / d) * force * 0.35;
          }
        }

        pt.vx = (pt.vx + ax) * 0.86;
        pt.vy = (pt.vy + ay) * 0.86;
        const px = pt.x;
        const py = pt.y;
        pt.x += pt.vx;
        pt.y += pt.vy;
        pt.life -= 1;

        const speed = Math.min(1, Math.hypot(pt.vx, pt.vy) / 2.4);
        ctx.strokeStyle = `rgba(${INK}, ${0.06 + speed * 0.5})`;
        ctx.lineWidth = 0.6 + speed * 1.1;
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(pt.x, pt.y);
        ctx.stroke();

        // respawn off-screen or aged-out particles
        if (
          pt.x < -10 || pt.x > width + 10 ||
          pt.y < -10 || pt.y > height + 10 ||
          pt.life < 0
        ) {
          pt.x = Math.random() * width;
          pt.y = Math.random() * height;
          pt.vx = 0;
          pt.vy = 0;
          pt.life = 120 + Math.random() * 220;
        }
      }
      rafRef.current = requestAnimationFrame(step);
    };

    const toLocal = (clientX, clientY) => {
      const rect = canvas.getBoundingClientRect();
      return { x: clientX - rect.left, y: clientY - rect.top };
    };
    const onMove = (e) => {
      const { x, y } = toLocal(e.clientX, e.clientY);
      pointer.current = { x, y, active: true };
    };
    const onLeave = () => {
      pointer.current.active = false;
    };
    const onTouch = (e) => {
      if (!e.touches.length) return;
      const { x, y } = toLocal(e.touches[0].clientX, e.touches[0].clientY);
      pointer.current = { x, y, active: true };
    };

    resize();
    rafRef.current = requestAnimationFrame(step);
    window.addEventListener("resize", resize);
    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerleave", onLeave);
    canvas.addEventListener("touchmove", onTouch, { passive: true });
    canvas.addEventListener("touchstart", onTouch, { passive: true });
    canvas.addEventListener("touchend", onLeave);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerleave", onLeave);
      canvas.removeEventListener("touchmove", onTouch);
      canvas.removeEventListener("touchstart", onTouch);
      canvas.removeEventListener("touchend", onLeave);
    };
  }, []);

  return (
    <div className="relative w-full overflow-hidden rounded-2xl border border-white/10 bg-surface">
      <canvas
        ref={canvasRef}
        data-testid="flowfield-canvas"
        className="block w-full h-[60vh] sm:h-[68vh] touch-none cursor-crosshair"
      />
      <p className="pointer-events-none absolute bottom-4 left-5 text-xs tracking-[0.15em] uppercase text-ash">
        Move your cursor
      </p>
    </div>
  );
}
